import { useEffect, useState } from "react";
import {
  Check, X, Clock, UserPlus, MoreVertical, Mail, Slash,
  ShieldCheck, RefreshCw, Pencil,
} from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ADMIN } from "@/constants/testIds";

// Phase 2J-AK — invitation workflow status palette. Internal DB status
// names ("invited", "approved", "pending", "disabled", "rejected") are
// preserved; only the rendered label changes for clarity.
const STATUS_STYLES = {
  invited:  { bg: "bg-cyan-50",    text: "text-cyan-700",    label: "Invited",  icon: Mail },
  pending:  { bg: "bg-amber-50",   text: "text-amber-700",   label: "Pending",  icon: Clock },
  approved: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Active",   icon: ShieldCheck },
  disabled: { bg: "bg-slate-200",  text: "text-slate-600",   label: "Disabled", icon: Slash },
  rejected: { bg: "bg-rose-50",    text: "text-rose-700",    label: "Rejected", icon: X },
};

const ROLES = [
  { value: "lab_technician",   label: "Lab Technician" },
  { value: "water_technician", label: "Water Technician" },
  { value: "admin",            label: "Admin" },
  { value: "trade_partner",    label: "Trade Partner" },
];

const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));

function StatusPill({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = style.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.text}`}>
      <Icon className="w-3 h-3" />
      {style.label}
    </span>
  );
}

function InviteModal({ open, onClose, onSubmit, submitting }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("lab_technician");

  if (!open) return null;
  const valid = email.trim() && name.trim() && role;
  return (
    <div data-testid="invite-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white max-w-md w-full rounded-3xl shadow-2xl overflow-hidden">
        <div className="bg-slate-900 text-white p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-300">Invite team member</div>
          <h2 className="font-display text-2xl font-bold mt-1">Add a new technician or admin</h2>
          <p className="text-sm text-slate-300 mt-2">
            They&apos;ll receive an email with a 7-day setup link to choose their password.
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Full name</label>
            <input
              data-testid="invite-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
              placeholder="Sam Technician"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</label>
            <input
              data-testid="invite-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
              placeholder="sam@example.com"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Role</label>
            <select
              data-testid="invite-role-select"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none bg-white"
            >
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </div>
        <div className="bg-slate-50 px-6 py-4 flex items-center justify-end gap-2 border-t border-slate-200">
          <button onClick={onClose} disabled={submitting} className="px-4 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-200 transition-colors">Cancel</button>
          <button
            data-testid="invite-submit-btn"
            onClick={() => onSubmit({ email: email.trim(), full_name: name.trim(), role })}
            disabled={!valid || submitting}
            className="px-5 py-2 rounded-full text-sm font-semibold bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-60 transition-colors"
          >
            {submitting ? "Sending…" : "Send invite"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Jun 2026 — Change-role dialog. Replaces the old window.prompt flow
 * (typo-prone free text) with a proper role dropdown.
 */
function ChangeRoleDialog({ user, onClose, onSaved }) {
  const [role, setRole] = useState(user?.role || "lab_technician");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRole(user?.role || "lab_technician"); }, [user]);
  if (!user) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${user.id}/role`, { role });
      toast.success(`${user.full_name} is now ${ROLE_LABEL[role] || role}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="change-role-dialog" className="max-w-md bg-white rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Change role</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-slate-900">{user.full_name}</span>
            <span className="text-slate-400"> · {user.email}</span>
          </p>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Role</label>
            <select
              data-testid="change-role-select"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none bg-white"
            >
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          {role === "admin" && user.role !== "admin" && (
            <p data-testid="change-role-admin-note" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Admins have full access to bookings, payments, lab results and team management.
              They&apos;ll need to sign out and back in for the new permissions to take effect.
            </p>
          )}
        </div>
        <DialogFooter>
          <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-200 transition-colors">Cancel</button>
          <button
            data-testid="change-role-save-btn"
            onClick={save}
            disabled={saving || role === user.role}
            className="px-5 py-2 rounded-full text-sm font-semibold bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-60 transition-colors"
          >
            {saving ? "Saving…" : "Save role"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Feb 2026 — Admin edit-user-details dialog.
 * Editable fields: full_name, email, phone, company_name. Other
 * account controls (role, status, wholesale_tier, partner link) are
 * intentionally out of scope and have their own dedicated dialogs /
 * endpoints.
 */
function EditUserDialog({ open, user, onOpenChange, onSaved, currentUserId }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setFullName(user.full_name || "");
      setEmail(user.email || "");
      setPhone(user.phone || "");
      setCompanyName(user.company_name || "");
    }
  }, [user]);

  if (!user) return null;

  const emailChanged = email.trim().toLowerCase() !== (user.email || "").toLowerCase();
  const isSelf = user.id === currentUserId;
  const canChangeEmail = !isSelf;
  const nothingChanged =
    fullName.trim() === (user.full_name || "") &&
    email.trim().toLowerCase() === (user.email || "").toLowerCase() &&
    (phone.trim() || "") === (user.phone || "") &&
    (companyName.trim() || "") === (user.company_name || "");

  const save = async () => {
    setSubmitting(true);
    try {
      // Only send fields that actually changed to keep the audit trail clean.
      const payload = {};
      if (fullName.trim() !== (user.full_name || "")) payload.full_name = fullName.trim();
      if (canChangeEmail && emailChanged) payload.email = email.trim().toLowerCase();
      if ((phone.trim() || "") !== (user.phone || "")) payload.phone = phone.trim();
      if ((companyName.trim() || "") !== (user.company_name || "")) payload.company_name = companyName.trim();
      const { data } = await api.patch(`/admin/users/${user.id}`, payload);
      toast.success(
        emailChanged && payload.email
          ? `Saved — heads-up email sent to ${data.email}`
          : `Saved changes to ${data.full_name}`,
      );
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Backend returns a structured detail for email conflicts.
      if (detail && typeof detail === "object" && detail.code === "email_in_use") {
        toast.error("That email is already used by another user.");
      } else {
        toast.error(formatApiError(detail) || e.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="edit-user-modal"
        className="sm:max-w-lg bg-white p-0 overflow-hidden rounded-2xl border border-slate-200"
      >
        <DialogHeader className="bg-slate-900 text-white p-6 space-y-1">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-300">
            Edit user details
          </div>
          <DialogTitle className="font-display text-xl font-bold text-white">
            {user.full_name || user.email}
          </DialogTitle>
          <p className="text-xs text-slate-300 pt-1">
            Update basic contact/login details only. Role, status &amp;
            partner linking are managed separately.
          </p>
        </DialogHeader>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Full name</label>
            <input
              data-testid="edit-user-fullname-input"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
              Login email {!canChangeEmail && <span className="normal-case font-normal text-slate-400">(cannot edit your own here)</span>}
            </label>
            <input
              data-testid="edit-user-email-input"
              type="email"
              value={email}
              disabled={!canChangeEmail}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
            />
            {canChangeEmail && emailChanged && (
              <p data-testid="edit-user-email-change-warning" className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                Changing the email will sign this user out and email a
                heads-up notice to the new address plus a security notice
                to the old one. Passwords are not affected.
              </p>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Phone</label>
            <input
              data-testid="edit-user-phone-input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+61 400 000 000"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Company / business name</label>
            <input
              data-testid="edit-user-company-input"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Optional"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
            />
          </div>
        </div>
        <DialogFooter className="bg-slate-50 px-6 py-4 border-t border-slate-200 gap-2 sm:justify-end">
          <button
            data-testid="edit-user-cancel-btn"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="px-4 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-200 transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="edit-user-save-btn"
            onClick={save}
            disabled={submitting || nothingChanged || !fullName.trim() || !email.trim()}
            className="px-5 py-2 rounded-full text-sm font-semibold bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-60 transition-colors"
          >
            {submitting ? "Saving…" : "Save changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [submittingInvite, setSubmittingInvite] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [roleUser, setRoleUser] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/users/${id}/${action}`);
      toast.success(`User ${action === "approve" ? "approved" : "rejected"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const submitInvite = async (payload) => {
    setSubmittingInvite(true);
    try {
      await api.post("/admin/users/invite", payload);
      toast.success(`Invite sent to ${payload.email}`);
      setInviteOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmittingInvite(false);
    }
  };

  const resendInvite = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/resend-invite`);
      toast.success(`Invite resent to ${u.email}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const toggleDisable = async (u) => {
    const action = u.status === "disabled" ? "enable" : "disable";
    if (action === "disable" && !window.confirm(`Disable ${u.full_name}? They will be signed out immediately.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/${action}`);
      toast.success(`User ${action}d`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <PortalShell title="Team members" subtitle="Aware Admin · Invite, manage roles, disable">
      <div className="mb-5 flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Invite technicians, assign roles, or disable accounts. Setup links expire after 7 days.</p>
        <button
          data-testid="open-invite-modal-btn"
          onClick={() => setInviteOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-600 text-white text-sm font-semibold hover:bg-cyan-700 transition-colors"
        >
          <UserPlus className="w-4 h-4" /> Invite team member
        </button>
      </div>
      {/* Feb 2026 — dropped `overflow-hidden` on the wrapper so per-row
          Manage menus (portal-rendered via shadcn DropdownMenu now)
          plus the sticky header don't clip the last rows. Rounded
          corners preserved by rounding the thead cells directly. */}
      <div data-testid={ADMIN.usersTable} className="rounded-3xl bg-white border border-slate-200">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading users…</div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No users yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500 rounded-tl-3xl">User</th>
                <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Role</th>
                <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Status</th>
                <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Invited</th>
                <th className="text-right p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500 rounded-tr-3xl">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-100 align-top">
                  <td className="p-4">
                    <div className="font-semibold text-slate-900">{u.full_name}</div>
                    <div className="text-xs text-slate-500 break-all">{u.email}</div>
                    {u.company_name && <div className="text-[11px] text-slate-400 mt-0.5">{u.company_name}</div>}
                  </td>
                  <td className="p-4">
                    <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </td>
                  <td className="p-4"><StatusPill status={u.status} /></td>
                  <td className="p-4 text-xs text-slate-500">
                    {u.invited_at ? (
                      <>
                        <div>{formatDateAU(u.invited_at)}</div>
                        {u.last_invite_sent_at && u.last_invite_sent_at !== u.invited_at && (
                          <div className="text-[10px] text-slate-400 mt-0.5">Resent {formatDateAU(u.last_invite_sent_at)}</div>
                        )}
                      </>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="p-4 text-right">
                    {/* Legacy approve/reject preserved for self-registered users */}
                    {u.role !== "admin" && u.status === "pending" && (
                      <div className="inline-flex items-center gap-2 mr-2">
                        <button data-testid={ADMIN.approveBtn(u.id)} onClick={() => act(u.id, "approve")} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 transition-colors">
                          <Check className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button data-testid={ADMIN.rejectBtn(u.id)} onClick={() => act(u.id, "reject")} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:border-rose-200 hover:text-rose-600 transition-colors">
                          <X className="w-3.5 h-3.5" /> Reject
                        </button>
                      </div>
                    )}

                    {/* Feb 2026 — Manage menu now uses shadcn DropdownMenu
                        (portal-rendered), so it never gets clipped by
                        the table wrapper regardless of row position. */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`user-row-menu-${u.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:border-slate-300 hover:bg-slate-50 transition-colors"
                        >
                          <MoreVertical className="w-3.5 h-3.5" /> Manage
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="w-52 bg-white border border-slate-200 shadow-xl rounded-xl py-1"
                      >
                        <DropdownMenuItem
                          data-testid={`user-row-edit-${u.id}`}
                          onSelect={() => setEditingUser(u)}
                          className="px-3 py-2 text-xs font-medium text-slate-700 hover:bg-cyan-50 cursor-pointer flex items-center gap-2"
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit details
                        </DropdownMenuItem>
                        {u.status === "invited" && (
                          <DropdownMenuItem
                            data-testid={`user-row-resend-${u.id}`}
                            onSelect={() => resendInvite(u)}
                            className="px-3 py-2 text-xs font-medium text-slate-700 hover:bg-cyan-50 cursor-pointer flex items-center gap-2"
                          >
                            <RefreshCw className="w-3.5 h-3.5" /> Resend invite
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          data-testid={`user-row-role-${u.id}`}
                          onSelect={() => setRoleUser(u)}
                          className="px-3 py-2 text-xs font-medium text-slate-700 hover:bg-cyan-50 cursor-pointer flex items-center gap-2"
                        >
                          <ShieldCheck className="w-3.5 h-3.5" /> Change role
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          data-testid={`user-row-toggle-${u.id}`}
                          onSelect={() => toggleDisable(u)}
                          className="px-3 py-2 text-xs font-medium text-rose-700 hover:bg-rose-50 cursor-pointer flex items-center gap-2"
                        >
                          <Slash className="w-3.5 h-3.5" />
                          {u.status === "disabled" ? "Re-enable user" : "Disable user"}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSubmit={submitInvite}
        submitting={submittingInvite}
      />

      <ChangeRoleDialog
        user={roleUser}
        onClose={() => setRoleUser(null)}
        onSaved={() => { setRoleUser(null); load(); }}
      />

      <EditUserDialog
        open={!!editingUser}
        user={editingUser}
        currentUserId={currentUser?.id}
        onOpenChange={(o) => { if (!o) setEditingUser(null); }}
        onSaved={() => load()}
      />
    </PortalShell>
  );
}
